import { ISensor } from "../Models/ISensor";
import { getEnvVar } from "../Config/AppConfig";

/**
 * Ottiene il timeout configurato dall'environment o usa il default
 * @returns Timeout in secondi per considerare un sensore offline
 */
const getSensorOfflineTimeout = (): number => {
  const envTimeout = getEnvVar("REACT_APP_SENSOR_OFF_TIMEOUT", "60");
  return parseInt(envTimeout, 10);
};

/**
 * Timeout in secondi per considerare un sensore offline
 * Può essere modificato tramite la variabile d'ambiente REACT_APP_SENSOR_OFF_TIMEOUT
 */
export const SENSOR_OFFLINE_TIMEOUT_SECONDS = getSensorOfflineTimeout();

/**
 * Verifica se un sensore è offline in base all'ultimo aggiornamento
 * @param sensor Il sensore da verificare
 * @param timeoutSeconds Timeout in secondi (default: valore da .env o 60)
 * @returns true se il sensore è offline, false altrimenti
 */
export const isSensorOffline = (
  sensor: ISensor,
  timeoutSeconds: number = SENSOR_OFFLINE_TIMEOUT_SECONDS
): boolean => {
  // I sensori disabilitati non sono considerati "offline"
  if (!sensor.enabled) return false;

  const TIMEOUT = 1000 * timeoutSeconds;
  const now = Date.now();
  const lastUpdate = new Date(sensor.updatedAt).getTime();

  return now - lastUpdate > TIMEOUT;
};

/**
 * Calcola il tempo trascorso dall'ultimo aggiornamento in formato leggibile
 * @param updatedAt Timestamp dell'ultimo aggiornamento
 * @returns Stringa formattata (es. "5 minuti fa", "2 ore fa")
 */
export const getTimeSinceLastUpdate = (updatedAt: string): string => {
  const now = Date.now();
  const lastUpdate = new Date(updatedAt).getTime();
  const diffMs = now - lastUpdate;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 1) {
    return "meno di 1 minuto fa";
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} minut${diffMinutes === 1 ? "o" : "i"} fa`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} or${diffHours === 1 ? "a" : "e"} fa`;
  }

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} giorn${diffDays === 1 ? "o" : "i"} fa`;
};

/**
 * Genera il contenuto del tooltip per un sensore offline
 * @param sensor Il sensore
 * @param sensorName Nome leggibile del sensore
 * @returns Contenuto del tooltip formattato
 */
export const getOfflineTooltipContent = (
  sensor: ISensor,
  sensorName: string
): string => {
  const timeAgo = getTimeSinceLastUpdate(sensor.updatedAt);
  const lastUpdateFormatted = new Date(sensor.updatedAt).toLocaleString(
    "it-IT",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  );

  return (
    `⚠️ ${sensorName} - OFFLINE\n` +
    `Ultima comunicazione: ${lastUpdateFormatted}\n` +
    `(${timeAgo})\n` +
    `Possibili cause: Alimentazione, WiFi, Malfunzionamento`
  );
};
